import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import {
  getSAVThreadById,
  loadSAVThreads,
  addSAVMessage,
  updateSAVStatus,
} from "../../data/sav";

const DEFAULT_REPLY_TEMPLATE = `Chère cliente, cher client,
Cordialement,
Le service client`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const statusBadgeClass = (status) => {
  switch (status) {
    case "Ouvert": return "bg-primary";
    case "En attente 1": return "bg-warning text-dark";
    case "En attente 2": return "bg-danger";
    case "Fermé": return "bg-secondary";
    default: return "bg-secondary";
  }
};

const TimelineMessage = ({ message }) => {
  const isClient = message.type === "client";
  return (
    <div className="sav-timeline-item">
      <div className={`sav-timeline-icon ${isClient ? "sav-timeline-icon-client" : "sav-timeline-icon-admin"}`}>
        <i className={`fas ${isClient ? "fa-user" : "fa-envelope"}`} />
      </div>
      <div className="sav-timeline-card card border-0 shadow-sm">
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span className={`badge ${isClient ? "bg-secondary" : "bg-primary"} me-2`}>{isClient ? "CLIENT" : "ADMIN"}</span>
              <strong className="small">{message.author}</strong>
            </div>
            <small className="text-muted">{formatDateTime(message.date)}</small>
          </div>
          <div className="sav-timeline-text" style={{ whiteSpace: "pre-wrap" }}>{message.content}</div>
        </div>
      </div>
    </div>
  );
};

const TimelineOrderUpdate = ({ order }) => (
  <div className="sav-timeline-item">
    <div className="sav-timeline-icon sav-timeline-icon-order">
      <i className="fas fa-truck" />
    </div>
    <div className="sav-timeline-card card border-0 shadow-sm">
      <div className="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <span className="badge bg-info text-dark me-2">COMMANDE</span>
          <strong className="small">Commande #{order.number || order.id}</strong>
          <span className="text-muted small ms-2">– {order.status}</span>
          <div className="text-muted small mt-1">{formatOrderAmount(order.total)} · {formatDateTime(order.date)}</div>
        </div>
        <Link to={`/MyAcconutPaymentMethod`} className="btn btn-sm btn-outline-primary">
          <i className="fas fa-eye me-1" />Plus de détails
        </Link>
      </div>
    </div>
  </div>
);

const formatOrderAmount = (v) => `${Number(v || 0).toFixed(2)} €`;

const MyAccountSAVDetail = () => {
  const { id } = useParams();
  const [loader, setLoader] = useState(true);
  const [thread, setThread] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState(DEFAULT_REPLY_TEMPLATE);
  const [transferTo, setTransferTo] = useState("");
  const [replySent, setReplySent] = useState(false);

  useEffect(() => {
    let isMounted = true;
    loadSAVThreads().then(() => {
      if (!isMounted) return;
      getSAVThreadById(id).then((t) => {
        if (!isMounted) return;
        if (!t) setNotFound(true);
        setThread(t);
        setLoader(false);
      });
    });
    return () => { isMounted = false; };
  }, [id]);

  const timeline = useMemo(() => {
    if (!thread) return [];
    const clientOrders = thread.orders || [];
    const entries = [
      ...(thread.messages || []).map((m) => ({ ...m, _kind: "message" })),
      ...clientOrders.map((o) => ({ ...o, _kind: "order", author: "Commande", date: o.date, content: "" })),
    ];
    return entries.sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  }, [thread]);

  if (loader) {
    return (
      <div>
        <ScrollToTop />
        <section className="admin-section">
          <div className="container">
            <div className="row">
              <AdminSidebar activePath="/MyAccountCustomers" />
              <div className="col-lg-9 col-md-8 col-12">
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">Chargement du fil de discussion...</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (notFound || !thread) {
    return (
      <div>
        <ScrollToTop />
        <section className="admin-section">
          <div className="container">
            <div className="row">
              <AdminSidebar activePath="/MyAccountCustomers" />
              <div className="col-lg-9 col-md-8 col-12">
                <div className="p-6 p-lg-10">
                  <div className="alert alert-warning">Fil de discussion introuvable #{id}.</div>
                  <Link to="/MyAccountCustomers" className="btn btn-outline-primary">Retour aux clients</Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const firstClientMessage =
    (thread.messages || []).find((m) => m.type === "client") || null;

  const handleChangeStatus = async (status) => {
    const updated = await updateSAVStatus(thread.id, status);
    const t = updated.find((x) => Number(x.id) === Number(thread.id));
    setThread(t);
  };

  const handleTransfer = async () => {
    if (!transferTo.trim()) return;
    const updated = await updateSAVStatus(thread.id, thread.status, transferTo.trim());
    const t = updated.find((x) => Number(x.id) === Number(thread.id));
    setThread(t);
    setTransferTo("");
  };

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    const updated = await addSAVMessage(thread.id, reply.trim(), "Admin");
    const t = updated.find((x) => Number(x.id) === Number(thread.id));
    setThread(t);
    setReply(DEFAULT_REPLY_TEMPLATE);
    setReplySent(true);
    setTimeout(() => setReplySent(false), 3000);
  };

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountCustomers" />
            <div className="col-lg-9 col-md-8 col-12">
              <div className="p-6 p-lg-10">
                <nav aria-label="breadcrumb" className="mb-3">
                  <ol className="breadcrumb mb-0 small">
                    <li className="breadcrumb-item"><Link to="/MyAccountCustomers">Clients</Link></li>
                    <li className="breadcrumb-item"><Link to="/MyAccountCustomers">SAV</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Afficher</li>
                  </ol>
                </nav>

                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                  <h2 className="mb-0 fs-3">FIL DE DISCUSSION: <span className="text-primary">#{thread.id}</span></h2>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => { const el = document.getElementById("sav-reply-textarea"); if (el) el.focus(); }}
                    >
                      <i className="fas fa-reply me-1" />Répondre au prochain message sans réponse
                    </button>
                  </div>
                </div>

                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body p-3 d-flex flex-wrap gap-2 align-items-center">
                    <button type="button" className="btn btn-success btn-sm" onClick={() => handleChangeStatus("Fermé")}>
                      <i className="fas fa-check me-1" />Marquer comme "traité"
                    </button>
                    <button type="button" className="btn btn-warning btn-sm text-dark" onClick={() => handleChangeStatus("En attente 1")}>
                      <i className="fas fa-clock me-1" />Marquer comme "en attente 1"
                    </button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => handleChangeStatus("En attente 2")}>
                      <i className="fas fa-clock me-1" />Marquer comme "en attente 2"
                    </button>
                    <div className="ms-auto d-flex gap-2 align-items-center">
                      <span className="badge" style={{ background: "#f1f3f5", color: "#495057" }}>Statut: <span className={`badge ${statusBadgeClass(thread.status)}`}>{thread.status}</span></span>
                    </div>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-lg-8">
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div className="sav-avatar">{thread.clientName.charAt(0).toUpperCase()}</div>
                          <div>
                            <h5 className="mb-0">{thread.clientName}</h5>
                            <div className="text-muted small">{thread.clientEmail}</div>
                          </div>
                          {thread.isPrivate && (
                            <span className="badge bg-dark ms-auto"><i className="fas fa-lock me-1" />Privé</span>
                          )}
                        </div>
                        <div className="row g-2 text-muted small mb-3">
                          <div className="col-md-4">Inscrit le {formatDate(thread.createdAt)}</div>
                          <div className="col-md-4">Type: {thread.type}</div>
                          <div className="col-md-4">Langue: {thread.language}</div>
                        </div>
                        <hr />
                        <div className="small">
                          <strong>Résumé de la demande</strong>
                          <p className="text-muted mb-0 mt-1">{firstClientMessage ? firstClientMessage.content : "—"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex flex-column gap-3 mb-4">
                      {timeline.map((entry, i) =>
                        entry._kind === "order" ? (
                          <TimelineOrderUpdate key={`o-${entry.id}-${i}`} order={entry} />
                        ) : (
                          <TimelineMessage key={`m-${entry.date}-${i}`} message={entry} />
                        )
                      )}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-light">
                        <strong className="small">INFORMATION CLIENT</strong>
                      </div>
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <div className="sav-avatar">{thread.clientName.charAt(0).toUpperCase()}</div>
                          <div>
                            <div className="fw-semibold">{thread.clientName}</div>
                            <div className="text-muted small">{thread.clientEmail}</div>
                          </div>
                        </div>
                        <div className="small text-muted">
                          {(thread.orders || []).length} commande(s) validée(s)
                          <div className="fw-bold text-dark">
                            pour un montant total de{" "}
                            {(thread.orders || []).reduce((s, o) => s + Number(o.total || 0), 0).toFixed(2)} €
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-light">
                        <strong className="small">DÉTAILS DU FIL</strong>
                      </div>
                      <div className="card-body p-3 small">
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Date de création</span>
                          <span>{formatDate(thread.createdAt)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Dernier message</span>
                          <span>{formatDateTime(thread.lastMessageDate)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Messages</span>
                          <span>{(thread.messages || []).length}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Employé</span>
                          <span>{thread.employee || "—"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-light">
                        <strong className="small">TRANSFÉRER LA DISCUSSION</strong>
                      </div>
                      <div className="card-body p-3">
                        <div className="d-flex gap-2">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Nom de l'employé"
                            value={transferTo}
                            onChange={(e) => setTransferTo(e.target.value)}
                          />
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleTransfer}>
                            Transférer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-header bg-light">
                    <strong className="small text-uppercase">Votre réponse à "{thread.clientName}"</strong>
                  </div>
                  <div className="card-body p-4">
                    {replySent && (
                      <div className="alert alert-success py-2">Réponse envoyée dans le fil de discussion.</div>
                    )}
                    <textarea
                      id="sav-reply-textarea"
                      className="form-control mb-3"
                      rows={6}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                    />
                    <button type="button" className="btn btn-primary" style={{ backgroundColor: '#e67e22', borderColor: '#e67e22' }} onClick={handleSendReply}>
                      <i className="fas fa-paper-plane me-1" />Envoyer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountSAVDetail;
